import { useState } from 'react'
import { api } from '../api'
import { CloudUpload, X } from './Icons'

type Props = {
  onClose: () => void
  onPublished: () => Promise<void>
}

type Stage =
  | 'idle'
  | 'hashing'
  | 'staging'
  | 'uploading'
  | 'verifying'
  | 'publishing'
  | 'published'

const stageLabels: Record<Exclude<Stage, 'idle' | 'published'>, string> = {
  hashing: 'Calculating SHA-256…',
  staging: 'Creating release draft…',
  uploading: 'Uploading package…',
  verifying: 'Verifying and signing artifact…',
  publishing: 'Publishing release…',
}

async function sha256(file: File) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

export function PublishDialog({ onClose, onPublished }: Props) {
  const [stage, setStage] = useState<Stage>('idle')
  const [error, setError] = useState('')
  const [file, setFile] = useState<File | null>(null)

  const busy = stage !== 'idle' && stage !== 'published'

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={busy ? undefined : onClose}>
      <section
        className="publish-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <h2 id="publish-title">Publish release</h2>
            <p>Upload, verify, sign, and publish an entitlement-aware package.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close dialog" disabled={busy}>
            <X size={18} />
          </button>
        </header>
        {stage === 'published' ? (
          <div className="publish-success">
            <span className="success-icon">✓</span>
            <h3>Release published</h3>
            <p>The artifact passed server-side SHA-256 verification and was signed.</p>
            <button className="primary-button" type="button" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <form
            onSubmit={async (event) => {
              event.preventDefault()
              if (!file) {
                setError('Choose an RBZ or ZIP package.')
                return
              }
              const form = new FormData(event.currentTarget)
              setError('')
              try {
                setStage('hashing')
                const digest = await sha256(file)
                setStage('staging')
                const release = await api.createRelease({
                  product_slug: String(form.get('product')),
                  version: String(form.get('version')),
                  channel: String(form.get('channel')),
                  release_notes: String(form.get('release_notes')),
                  mandatory: form.get('mandatory') === 'on',
                })
                const staged = await api.createArtifact(release.id, {
                  filename: file.name,
                  content_type: file.type || 'application/octet-stream',
                  host: String(form.get('host')),
                  host_version_min: Number(form.get('host_version_min')),
                  host_version_max: Number(form.get('host_version_max')),
                  platform: String(form.get('platform')),
                  architecture: String(form.get('architecture')),
                  ruby_abi:
                    String(form.get('ruby_abi') ?? '').trim() || undefined,
                  sha256: digest,
                  size_bytes: file.size,
                })
                setStage('uploading')
                await api.uploadArtifact(staged.upload, file)
                setStage('verifying')
                await api.finalizeArtifact(staged.artifact.id)
                setStage('publishing')
                await api.publishRelease(release.id)
                await onPublished()
                setStage('published')
              } catch (publishError) {
                setError(
                  publishError instanceof Error
                    ? publishError.message
                    : 'Release publishing failed',
                )
                setStage('idle')
              }
            }}
          >
            <label>
              Product
              <select name="product" defaultValue="curic_studio" disabled={busy}>
                <option value="curic_studio">Curic Studio</option>
                <option value="curic_section">Curic Section</option>
                <option value="curic_scene_manager">Curic Scene Manager</option>
                <option value="curic_publish">Curic Publish</option>
              </select>
            </label>
            <div className="form-row">
              <label>
                Version
                <input
                  name="version"
                  required
                  defaultValue="2.5.0-beta.4"
                  placeholder="2.5.0-beta.4"
                  title="Semantic Versioning value, for example 2.5.0-beta.4"
                  disabled={busy}
                />
              </label>
              <label>
                Channel
                <select name="channel" defaultValue="stable" disabled={busy}>
                  <option>stable</option>
                  <option>beta</option>
                  <option>nightly</option>
                  <option>internal</option>
                </select>
              </label>
            </div>
            <label>
              Release notes
              <textarea
                name="release_notes"
                required
                defaultValue="SketchUp 2026 compatibility and performance improvements."
                disabled={busy}
              />
            </label>
            <fieldset className="target-fields" disabled={busy}>
              <legend>Artifact target</legend>
              <div className="form-row three-columns">
                <label>
                  Host
                  <select name="host" defaultValue="sketchup">
                    <option value="sketchup">SketchUp</option>
                    <option value="blender">Blender</option>
                  </select>
                </label>
                <label>
                  Platform
                  <select name="platform" defaultValue="macos">
                    <option value="macos">macOS</option>
                    <option value="windows">Windows</option>
                    <option value="linux">Linux</option>
                  </select>
                </label>
                <label>
                  Architecture
                  <select name="architecture" defaultValue="arm64">
                    <option value="arm64">ARM64</option>
                    <option value="x64">x64</option>
                  </select>
                </label>
              </div>
              <div className="form-row three-columns">
                <label>
                  Host from
                  <input name="host_version_min" type="number" defaultValue="2024" />
                </label>
                <label>
                  Host through
                  <input name="host_version_max" type="number" defaultValue="2026" />
                </label>
                <label>
                  Ruby ABI
                  <input name="ruby_abi" defaultValue="3.2" placeholder="Optional" />
                </label>
              </div>
            </fieldset>
            <label className="upload-field">
              <CloudUpload size={22} />
              <span>
                <strong>{file?.name ?? 'Choose a release artifact'}</strong>
                <small>
                  {file
                    ? `${(file.size / 1024 / 1024).toFixed(1)} MB · server verification enabled`
                    : 'RBZ or ZIP · SHA-256 will be calculated'}
                </small>
              </span>
              <input
                name="artifact"
                type="file"
                accept=".rbz,.zip"
                disabled={busy}
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null)
                  setError('')
                }}
              />
            </label>
            <label className="mandatory-field">
              <input name="mandatory" type="checkbox" disabled={busy} />
              <span>Mark this update as mandatory</span>
            </label>
            {error && <p className="publish-error" role="alert">{error}</p>}
            {busy && (
              <div className="publish-progress" role="status">
                <span className="progress-spinner" />
                {stageLabels[stage as Exclude<Stage, 'idle' | 'published'>]}
              </div>
            )}
            <footer>
              <button
                className="secondary-button"
                type="button"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button className="primary-button" type="submit" disabled={busy}>
                {busy ? 'Working…' : 'Publish release'}
              </button>
            </footer>
          </form>
        )}
      </section>
    </div>
  )
}
